import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Loader2,
  Clock, MoreVertical
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import Sidebar from "../components/Sidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORM_COLORS = {
  tiktok: "bg-pink-500",
  instagram: "bg-purple-500",
  linkedin: "bg-blue-600",
  twitter: "bg-sky-500",
  youtube: "bg-red-500"
};

export default function CalendarView() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [filterCampaign, setFilterCampaign] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [postsRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/calendar/posts`, { headers }).catch(() => ({ data: { posts: [] } })),
        axios.get(`${API}/campaigns/`, { headers })
      ]);
      
      // Calendar posts can come from calendar/posts OR from content with scheduled_at
      let posts = postsRes.data?.posts || postsRes.data || [];
      
      // Also fetch content from all campaigns to get scheduled content
      const campaigns = Array.isArray(campaignsRes.data) ? campaignsRes.data : [];
      setCampaigns(campaigns);
      
      // Get scheduled content from campaigns
      const contentPromises = campaigns.map(c => 
        axios.get(`${API}/campaigns/${c.id}/content`, { headers }).catch(() => ({ data: [] }))
      );
      const contentResults = await Promise.all(contentPromises);
      
      // Combine calendar posts with scheduled content
      const scheduledContent = contentResults.flatMap((res, i) => 
        (res.data || [])
          .filter(item => item.scheduled_at)
          .map(item => ({
            id: item.id,
            campaign_id: campaigns[i]?.id,
            campaign_name: campaigns[i]?.name,
            title: item.topic || item.hook || 'Untitled',
            content: item.caption || item.script || '',
            platform: item.platforms?.[0] || 'tiktok',
            scheduled_time: item.scheduled_at,
            status: item.status || 'scheduled'
          }))
      );
      
      // Merge and dedupe
      const allPosts = [...posts, ...scheduledContent];
      setScheduledPosts(allPosts);
      
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  };

  const getPostsForDate = (date) => {
    return scheduledPosts.filter(post => {
      const postDate = new Date(post.scheduled_time);
      return postDate.toDateString() === date.toDateString() &&
        (filterCampaign === "all" || post.campaign_id === filterCampaign);
    });
  };

  const getPostDays = () => {
    const days = [];
    scheduledPosts.forEach(post => {
      if (filterCampaign === "all" || post.campaign_id === filterCampaign) {
        const date = new Date(post.scheduled_time);
        days.push(date);
      }
    });
    return days;
  };

  const selectedDatePosts = getPostsForDate(selectedDate);

  const getCampaignName = (campaignId) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    return campaign?.name || "Unknown Campaign";
  };

  return (
    <div className="min-h-screen flex bg-background">
      <Sidebar active="calendar" />
      
      <main className="flex-1 md:ml-64 p-6 lg:p-8 overflow-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Content Calendar</h1>
            <p className="text-muted-foreground mt-1">View and manage your scheduled posts</p>
          </div>
          
          <Select value={filterCampaign} onValueChange={setFilterCampaign}>
            <SelectTrigger className="w-[200px] bg-white/5 border-white/10" data-testid="campaign-filter">
              <SelectValue placeholder="Filter by Campaign" />
            </SelectTrigger>
            <SelectContent className="glass border-white/10">
              <SelectItem value="all">All Campaigns</SelectItem>
              {campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar */}
            <Card className="glass-card border-white/5 lg:col-span-2">
              <CardContent className="p-6">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-md"
                  modifiers={{
                    hasPost: getPostDays()
                  }}
                  modifiersStyles={{
                    hasPost: {
                      backgroundColor: 'rgba(0, 229, 255, 0.2)',
                      borderRadius: '50%'
                    }
                  }}
                  data-testid="calendar"
                />
                
                {/* Legend */}
                <div className="mt-6 flex flex-wrap gap-4">
                  {Object.entries(PLATFORM_COLORS).map(([platform, color]) => (
                    <div key={platform} className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${color}`} />
                      <span className="text-sm text-muted-foreground capitalize">{platform}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Selected Date Posts */}
            <Card className="glass-card border-white/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-primary" />
                  {selectedDate.toLocaleDateString('en-US', { 
                    weekday: 'long',
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedDatePosts.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No posts scheduled for this day</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedDatePosts.map((post, i) => (
                      <motion.div
                        key={post.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/5"
                        data-testid={`scheduled-post-${i}`}
                      >
                        <div className={`w-2 h-8 rounded-full ${PLATFORM_COLORS[post.platform]}`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm capitalize">{post.platform}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {getCampaignName(post.campaign_id)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-mono">
                            {new Date(post.scheduled_time).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              post.status === 'posted' ? 'status-completed' : 'status-draft'
                            }`}
                          >
                            {post.status}
                          </Badge>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Posts */}
            <Card className="glass-card border-white/5 lg:col-span-3">
              <CardHeader>
                <CardTitle>Upcoming Posts</CardTitle>
              </CardHeader>
              <CardContent>
                {scheduledPosts.filter(p => 
                  new Date(p.scheduled_time) > new Date() &&
                  (filterCampaign === "all" || p.campaign_id === filterCampaign)
                ).length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">No upcoming posts scheduled</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => navigate("/dashboard")}
                    >
                      Create a Campaign
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {scheduledPosts
                      .filter(p => 
                        new Date(p.scheduled_time) > new Date() &&
                        (filterCampaign === "all" || p.campaign_id === filterCampaign)
                      )
                      .sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time))
                      .slice(0, 9)
                      .map((post, i) => (
                        <motion.div
                          key={post.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.03 }}
                          className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/5 card-hover cursor-pointer"
                          onClick={() => navigate(`/campaign/${post.campaign_id}`)}
                          data-testid={`upcoming-post-${i}`}
                        >
                          <div className={`w-10 h-10 rounded-lg ${PLATFORM_COLORS[post.platform]} flex items-center justify-center`}>
                            <span className="text-white font-bold text-xs uppercase">
                              {post.platform.slice(0, 2)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {getCampaignName(post.campaign_id)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(post.scheduled_time).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
